import { useState, type JSX } from 'react';
import { Link } from 'react-router-dom';
import { deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../hooks/useAuth';
import { cloudinaryResized } from '../lib/cloudinary';
import { formatDimensions } from '../lib/formatDimensions';
import type { Painting } from '../types/painting';

// One fixed width covers the grid reasonably across breakpoints (looks sharp
// on retina at the ~250-300px column width columns settle to) without the
// complexity of a full responsive srcset for what's a fairly small gallery.
const GRID_THUMBNAIL_WIDTH = 600;

// Where the single category tag ("Oil on Canvas" etc.) sits, decided by
// MasonryGrid based on measured available space: alongside the title,
// alongside the style/size row, or — when neither has room — its own row
// below, same as before this existed.
export type CategoryPlacement = 'title' | 'styleRow' | 'stacked';

interface ImageItemProps {
  painting: Painting;
  onSelect?: (painting: Painting) => void;
  priority?: boolean;
  categoryPlacement?: CategoryPlacement;
}

function CategoryPill({ tag }: { tag: string }): JSX.Element {
  return (
    <span className="shrink-0 rounded-full border border-neutral-900 px-2.5 py-0.5 text-xs text-neutral-900">
      {tag}
    </span>
  );
}

export default function ImageItem({
  painting,
  onSelect,
  priority = false,
  categoryPlacement = 'stacked',
}: ImageItemProps): JSX.Element {
  const { user } = useAuth();
  const [adminError, setAdminError] = useState<string | null>(null);
  const isSold = painting.status === 'sold';

  function handleToggleStatus(): void {
    setAdminError(null);
    const nextStatus = isSold ? 'available' : 'sold';
    updateDoc(doc(db, 'paintings', painting.id), { status: nextStatus }).catch((updateError: unknown): void => {
      setAdminError(updateError instanceof Error ? updateError.message : 'Could not update status.');
    });
  }

  function handleDelete(): void {
    if (!window.confirm(`Delete "${painting.title}"? This can't be undone.`)) return;
    setAdminError(null);
    deleteDoc(doc(db, 'paintings', painting.id)).catch((deleteError: unknown): void => {
      setAdminError(deleteError instanceof Error ? deleteError.message : 'Could not delete painting.');
    });
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => {
          onSelect?.(painting);
        }}
        className="block w-full text-left"
      >
        <div className="relative overflow-hidden rounded-md">
          <img
            src={cloudinaryResized(painting.imageUrl, GRID_THUMBNAIL_WIDTH)}
            alt={painting.title}
            className="w-full rounded-md"
            loading={priority ? 'eager' : 'lazy'}
            fetchPriority={priority ? 'high' : 'auto'}
            style={
              painting.imageWidth !== undefined && painting.imageHeight !== undefined
                ? { aspectRatio: `${painting.imageWidth} / ${painting.imageHeight}` }
                : undefined
            }
          />
          {isSold ? (
            <img src="/sold-stamp.avif" alt="Sold out" className="absolute right-1 top-1 w-1/5" />
          ) : null}
        </div>

        <div className="mt-2">
          {categoryPlacement === 'title' && painting.category[0] !== undefined ? (
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-2xl font-bold text-neutral-900">{painting.title}</h3>
              <CategoryPill tag={painting.category[0]} />
            </div>
          ) : (
            <h3 className="text-2xl font-bold text-neutral-900">{painting.title}</h3>
          )}

          <div className="flex items-center justify-between text-sm text-neutral-600">
            <span>Style: {painting.style.join(', ')}</span>
            {categoryPlacement === 'styleRow' && painting.category[0] !== undefined ? (
              <div className="flex items-center gap-3">
                <CategoryPill tag={painting.category[0]} />
                <span>{formatDimensions(painting.height, painting.width)}</span>
              </div>
            ) : (
              <span>{formatDimensions(painting.height, painting.width)}</span>
            )}
          </div>

          {categoryPlacement === 'stacked' && painting.category.length > 0 ? (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {painting.category.map((tag) => (
                <CategoryPill key={tag} tag={tag} />
              ))}
            </div>
          ) : null}
        </div>
      </button>

      {user !== null ? (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <Link
            to={`/admin/edit/${painting.id}`}
            state={{ painting }}
            className="rounded border border-neutral-300 px-2.5 py-1 text-xs text-neutral-700 hover:bg-neutral-100"
          >
            Edit
          </Link>
          <button
            type="button"
            onClick={handleToggleStatus}
            className="rounded border border-neutral-300 px-2.5 py-1 text-xs text-neutral-700 hover:bg-neutral-100"
          >
            Mark {isSold ? 'available' : 'sold'}
          </button>
          <button
            type="button"
            onClick={handleDelete}
            className="rounded border border-red-300 px-2.5 py-1 text-xs text-red-700 hover:bg-red-50"
          >
            Delete
          </button>
          {adminError !== null ? <p className="w-full text-xs text-red-600">{adminError}</p> : null}
        </div>
      ) : null}
    </div>
  );
}
